/**
 * Each-way fraction as a number.
 *
 * Replaces `eval()` on the same string. The value comes from account settings and race
 * data ("1/4", "1/5", "2/5"), so a fraction is all that ever needs evaluating.
 *
 * Malformed input returns 1 rather than NaN: with 1, the each-way leg contributes
 * ((odds - 1) * 1 + 1) * amount === odds * amount, i.e. a win-only return, so one bad
 * fraction cannot poison a whole betslip total.
 */
export function parseEachWayFraction(fraction: unknown): number {
  if (typeof fraction === 'number') {
    return Number.isFinite(fraction) ? fraction : 1;
  }

  if (typeof fraction !== 'string') {
    return 1;
  }

  const parts = fraction.trim().split('/');

  // A plain number ("0.25") evaluated to itself before, so it still does.
  if (parts.length === 1) {
    const single = parts[0].trim();

    if (single === '') {
      return 1;
    }

    const value = Number(single);

    return Number.isFinite(value) ? value : 1;
  }

  if (parts.length !== 2) {
    return 1;
  }

  const numerator = Number(parts[0]);
  const denominator = Number(parts[1]);

  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) {
    return 1;
  }

  return numerator / denominator;
}
